import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function VocalProcessingChainLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What a vocal chain does",
      text: "A vocal chain controls level, tone, dynamics, sibilance, character, and space so the vocal feels clear and finished.",
      icon: <MicRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "What matters most",
      text: "Start with the source and level consistency. A good chain cannot fully fix a vocal that is poorly recorded or wildly uneven.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Clean and control the vocal before adding character, brightness, width, or reverb.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const commonProblems = [
    {
      title: "The vocal gets processed but not better",
      description:
        "This usually happens when plugins are added without a clear reason. Every step should solve a problem or add something useful.",
    },
    {
      title: "The vocal is bright but still unclear",
      description:
        "Brightness is not the same as intelligibility. A vocal may need level control, low-mid cleanup, or less masking from the mix.",
    },
    {
      title: "Compression makes the vocal harsh",
      description:
        "Compression can push sibilance, breath noise, and upper-mid harshness forward if the tone is not controlled.",
    },
    {
      title: "Reverb pushes the vocal back",
      description:
        "Too much space, or too much low end in the reverb, can make the vocal feel distant instead of connected.",
    },
  ];

  const chainSteps = [
    {
      title: "Editing and cleanup",
      description:
        "Start before the plugin chain. Remove obvious noise, clean up distracting breaths if needed, fix clicks, and trim silence. You do not have to make the vocal sterile, but you want the chain reacting to the performance instead of avoidable problems.",
    },
    {
      title: "Clip gain or leveling",
      description:
        "Bring the vocal into a more consistent range before heavy compression. Clip gain, automation, or an auto leveler can reduce big level swings so the rest of the chain works more smoothly.",
    },
    {
      title: "Corrective EQ",
      description:
        "Use EQ to remove rumble, low-mid buildup, boxiness, or resonant problems. This stage is about clearing space and improving the source before compression and tone shaping.",
    },
    {
      title: "Compression",
      description:
        "Compression helps the vocal stay forward and controlled. It can smooth peaks, add density, and keep phrases from falling out of the mix. The goal is control without making the vocal feel pinned down.",
    },
    {
      title: "De-essing",
      description:
        "A de-esser controls sharp S, T, and SH sounds. This is especially useful after compression, because compression can bring sibilance forward along with the rest of the vocal.",
    },
    {
      title: "Tone shaping EQ",
      description:
        "After the vocal is controlled, use EQ to shape the final tone. This might mean adding presence for clarity, air for openness, or making small cuts where the vocal still feels cloudy.",
    },
    {
      title: "Saturation or character",
      description:
        "Subtle saturation can add density and harmonic detail so the vocal reads better without simply turning it up. Use it carefully; too much can make the vocal gritty or crowded.",
    },
    {
      title: "Reverb and delay",
      description:
        "Space effects help the vocal belong in the track. Reverb adds depth, delay adds size and movement, and both should be filtered so they do not cloud the vocal or mix.",
    },
    {
      title: "Automation",
      description:
        "Automation is often the final step that makes a vocal feel finished. Ride phrase endings, emotional words, and transitions so the performance stays connected from start to finish.",
    },
  ];

  const chainTools = [
    {
      title: "High-pass filter",
      description:
        "Removes rumble, handling noise, and unnecessary low end before it hits the rest of the chain.",
    },
    {
      title: "EQ",
      description:
        "Shapes clarity, body, presence, and air while reducing resonances or low-mid buildup.",
    },
    {
      title: "Compression",
      description:
        "Controls dynamics and helps the vocal stay present against the instrumental.",
    },
    {
      title: "De-esser",
      description:
        "Reduces sharp sibilance without darkening the entire vocal.",
    },
    {
      title: "Saturation",
      description:
        "Adds density, edge, or harmonic detail so the vocal feels more forward and finished.",
    },
    {
      title: "Reverb and delay",
      description:
        "Place the vocal in a space and help it connect to the production.",
    },
  ];

  const frequencyAreas = [
    {
      range: "80–150 Hz",
      title: "Rumble and proximity",
      description:
        "Too much energy here can make the vocal boomy or interfere with the low end of the mix.",
    },
    {
      range: "200–500 Hz",
      title: "Body and mud",
      description:
        "Adds warmth and size, but too much can make the vocal cloudy, boxy, or hard to place.",
    },
    {
      range: "1–3 kHz",
      title: "Intelligibility",
      description:
        "Helps words read clearly. Too much can become nasal or pushy.",
    },
    {
      range: "4–8 kHz",
      title: "Presence and sibilance",
      description:
        "Adds detail and edge, but can become sharp or tiring if uncontrolled.",
    },
    {
      range: "10 kHz+",
      title: "Air",
      description:
        "Adds openness and lift, but works best after harshness and sibilance are handled.",
    },
  ];

  const bestPractices = [
    "Do not copy a vocal chain blindly. Start with what the vocal actually needs.",
    "Level the vocal before asking compression to do all the work.",
    "Use corrective EQ before compression when resonances or low-mid buildup are causing problems.",
    "Control sibilance before adding a lot of brightness or air.",
    "Use reverb and delay on sends so you can shape the space separately.",
    "Make tone decisions while listening to the full track, not only in solo.",
    "Use automation at the end to keep the performance alive and connected.",
  ];

  const relatedLinks = [
    { label: "How to Make Vocals Sit in the Mix", to: "/learning/make-vocals-sit-in-mix" },
    { label: "How to Keep Audio Levels Consistent", to: "/learning/how-to-keep-audio-levels-consistent" },
    { label: "Compression", to: "/learning/compression" },
    { label: "De-Esser", to: "/learning/de-esser" },
    { label: "Equalization", to: "/learning/equalization" },
    { label: "Reverb", to: "/learning/reverb" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Vocal Processing Chain Explained | Parish Audio Learning"
        description="Learn how a vocal processing chain works, including editing, leveling, EQ, compression, de-essing, saturation, reverb, delay, and automation."
        canonical="https://parishaudio.com/learning/vocal-processing-chain"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about vocal processing chains"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/vocal-processing-chain"
        headline="Vocal Processing Chain Explained"
        description="Learn how a vocal processing chain works, including editing, leveling, EQ, compression, de-essing, saturation, reverb, delay, and automation."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        keywords={[
          "vocal processing chain",
          "vocal chain",
          "how to mix vocals",
          "vocal compression",
          "vocal EQ",
          "de-esser",
          "vocal saturation",
          "vocal reverb",
          "music production",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "Vocal Processing Chain Explained",
            item: "https://parishaudio.com/learning/vocal-processing-chain",
          },
        ]}
        datePublished="2026-05-02T00:00:00-06:00"
        dateModified="2026-05-02T00:00:00-06:00"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Vocal Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Vocal Processing Chain Explained
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 740,
                      }}
                    >
                      A vocal chain is not just a stack of plugins. It is a series of
                      decisions that help the vocal stay clear, controlled, and connected
                      to the song. The right chain solves problems in order, so each stage
                      makes the next one easier.
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Published May 2, 2026
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5, lineHeight: 1.2 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      What Is a Vocal Processing Chain?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A vocal processing chain is the path a vocal takes through editing,
                    level control, EQ, compression, de-essing, tone shaping, and effects.
                    The order matters because each processor changes what the next one
                    receives. A cleaner, more controlled vocal going into compression will
                    usually compress more naturally. A de-esser placed after compression
                    may catch sibilance that compression brings forward.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    There is no single chain that works for every voice. A soft, intimate
                    vocal needs a different approach than an aggressive rock vocal or a
                    spoken podcast track. The goal is to understand what each stage is
                    doing so you can build the chain around the source instead of forcing
                    the source into a preset.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Common Vocal Chain Problems
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {commonProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.5 }}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {problem.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Step-by-Step Vocal Chain
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {chainSteps.map((step, index) => (
                        <Paper key={step.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Useful Vocal Frequency Areas
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      These ranges are starting points. Always make the final choice while
                      listening to the vocal in the full mix.
                    </Typography>

                    <Stack spacing={1.2}>
                      {frequencyAreas.map((area) => (
                        <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.7}>
                            <Chip
                              label={area.range}
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: 700,
                              }}
                            />
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {area.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {area.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Why Order Matters
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Plugin order is not about following a rule for the sake of it. It is
                    about what each processor is reacting to. If compression comes before
                    cleanup EQ, it may react to rumble or low-mid buildup. If brightness is
                    added before de-essing, the sibilance may become harder to control.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A practical order is usually: clean up the source, control the level,
                    shape the tone, manage sibilance, add character, then place the vocal
                    in space. That order is flexible, but it gives you a reliable starting
                    point when a vocal feels hard to mix.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WavesRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Main Tools in a Vocal Chain
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {chainTools.map((tool) => (
                        <Paper key={tool.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.4 }}>
                            {tool.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {tool.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          Bypass the whole vocal chain, then bring it back in at a similar
                          level. If the vocal is clearer, steadier, and more connected
                          without only being louder, the chain is doing its job.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Tools That Help
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Vocal Pro is built around the idea of keeping the vocal chain focused.
                    Instead of loading a long list of separate processors, it gives you a
                    practical way to shape and control vocal tracks from one place.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Auto Level can also be useful before compression when the vocal has
                    big level swings. It helps smooth the broad movement so the compressor
                    can focus more on peaks, density, and feel.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/products/vocal-pro"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      View Vocal Pro
                    </Button>

                    <Button
                      component={RouterLink}
                      to="/products/auto-level"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}`,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      View Auto Level
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                      Related Topics
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    These articles connect directly to vocal processing, tone, dynamics,
                    and space.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {relatedLinks.map((link) => (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}